import { useMemo, useState } from 'react'
import {
  Bar, CartesianGrid, ComposedChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import type { CostIncomingWeek, CostTermsGroup } from '../../../lib/api'
import { useFormatters } from '../../../contexts/PreferencesContext'
import { termsDataKey } from './termsPalette'

/** What a click on the chart opens: the week, and the terms tab to start on. */
export interface WeekPick {
  week: CostIncomingWeek
  terms: string
  /** CR-1421 decision 4: an arrears bar opens only the week's arrears lines. */
  subset?: 'arrears'
}

interface IncomingPerWeekChartProps {
  weeks: CostIncomingWeek[]
  groups: CostTermsGroup[]
  colours: Record<string, string>
  mean: number | null
  onPick: (pick: WeekPick) => void
  /** Legend show/hide (CR-1421 item 5), keyed by legend label. */
  isHidden?: (label: string) => boolean
}

type Row = CostIncomingWeek & Record<string, number | string | null | object>

// Design decision (Sarah, 19 Oct 2026): legacy's order and colours, arrears first
// (Dashboard.js series colours #C00000, #FF8585, #3A65B9, #CAEEFB). The value bar keeps
// the payment-terms split (CR-1421), so only the lines bar takes legacy's blue.
const FIXED = {
  arrears_value: { colour: '#C00000', label: 'Arrears to Date Value' },
  arrears_lines: { colour: '#FF8585', label: 'Arrears to Date PO Lines' },
  lines: { colour: '#CAEEFB', label: 'Total Incoming PO Lines' },
} as const

interface TipProps {
  active?: boolean
  payload?: Array<{ dataKey?: string | number; payload?: Row }>
  groups: CostTermsGroup[]
  colours: Record<string, string>
  /** The bar under the pointer: recharts' payload names the first series, not the one hovered. */
  hovered: string | null
}

/**
 * CR-1421 decision 1b: the box names the bar hovered, not the whole week. The
 * value bar's box gives its total with the terms split beneath it.
 */
function BarTip({ active, payload, groups, colours, hovered }: TipProps) {
  const { formatCurrency } = useFormatters()
  const row = payload?.[0]?.payload
  if (!active || !row || !hovered) return null
  const key = hovered
  let body
  if (key.startsWith('t_')) {
    body = (
      <>
        <div className="flex justify-between gap-4 font-medium">
          <span>Total Incoming Value</span><span className="tabular-nums">{formatCurrency(row.value)}</span>
        </div>
        {groups.filter((g) => row.terms[g.key]).map((g) => (
          <div key={g.key} className="flex justify-between gap-4 pl-3 text-slate-600">
            <span className="flex items-center gap-1.5">
              <i className="inline-block h-2 w-2 rounded-sm" style={{ background: colours[g.key] }} />on {g.label}
            </span>
            <span className="tabular-nums">{formatCurrency(row.terms[g.key].value)}</span>
          </div>
        ))}
      </>
    )
  } else if (key === 'arrears_value') {
    body = (
      <>
        <div className="flex justify-between gap-4"><span>{FIXED.arrears_value.label}</span><span className="tabular-nums">{formatCurrency(row.arrears_value)}</span></div>
        <div className="text-xs text-muted-foreground">Open lines past their promise date</div>
      </>
    )
  } else if (key === 'lines') {
    body = <div className="flex justify-between gap-4"><span>{FIXED.lines.label}</span><span className="tabular-nums">{row.lines}</span></div>
  } else {
    body = (
      <>
        <div className="flex justify-between gap-4"><span>{FIXED.arrears_lines.label}</span><span className="tabular-nums">{row.arrears_lines}</span></div>
        <div className="text-xs text-muted-foreground">Open lines past their promise date</div>
      </>
    )
  }
  return (
    <div className="min-w-[200px] rounded-md border bg-white px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-semibold">{row.label}</div>
      {body}
    </div>
  )
}

/**
 * CR-1421: "Incoming Per Week by Latest Supplier Promise Date", looking forward.
 *
 * Four bars a week in legacy's order: Arrears value, Arrears lines (this week
 * only, every open line past its promise date), then Total Incoming value,
 * stacked by the supplier code's payment terms, and Total Incoming lines; and
 * the legacy Mean Average line. Clicking a coloured section opens the
 * click-through on that terms tab.
 */
export function IncomingPerWeekChart({ weeks, groups, colours, mean, onPick, isHidden = () => false }: IncomingPerWeekChartProps) {
  const { formatCompactCurrency, formatCurrency } = useFormatters()
  const [hovered, setHovered] = useState<string | null>(null)
  const hover = (key: string) => ({ onMouseEnter: () => setHovered(key), onMouseLeave: () => setHovered(null) })
  const rows = useMemo<Row[]>(() => weeks.map((w) => {
    const row: Row = { ...w }
    for (const g of groups) row[termsDataKey(g.key)] = w.terms[g.key]?.value ?? 0
    return row
  }), [weeks, groups])

  const pick = (terms: string, subset?: 'arrears') => (data: { payload?: Row }) => {
    if (data?.payload) onPick({ week: data.payload, terms, subset })
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={rows} style={{ cursor: 'pointer' }} margin={{ top: 16, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={52} />
        {/* The axis always reaches the Mean Average, so the line shows on a page whose bars are all below it. */}
        <YAxis yAxisId="value" tick={{ fontSize: 11 }} tickFormatter={(v: number) => formatCompactCurrency(v)}
          domain={[0, (dataMax: number) => Math.max(dataMax, mean ?? 0)]} />
        <YAxis yAxisId="lines" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} />
        <Tooltip shared={false} cursor={{ fill: 'rgba(59, 130, 246, 0.08)' }}
          content={<BarTip groups={groups} colours={colours} hovered={hovered} />} />
        {/* Every bar has a stackId: recharts 3 draws stacked bars before unstacked ones
            (barSelectors combineBarSizeList), so a lone stack per bar keeps this order. */}
        <Bar yAxisId="value" stackId="arrears_value" dataKey="arrears_value" name={FIXED.arrears_value.label} fill={FIXED.arrears_value.colour} radius={[4, 4, 0, 0]} onClick={pick('all', 'arrears')} {...hover('arrears_value')}
          hide={isHidden(FIXED.arrears_value.label)} />
        <Bar yAxisId="lines" stackId="arrears_lines" dataKey="arrears_lines" name={FIXED.arrears_lines.label} fill={FIXED.arrears_lines.colour} radius={[4, 4, 0, 0]} onClick={pick('all', 'arrears')} {...hover('arrears_lines')}
          hide={isHidden(FIXED.arrears_lines.label)} />
        {groups.map((g, i) => (
          <Bar key={g.key} yAxisId="value" stackId="value" dataKey={termsDataKey(g.key)} name={g.label}
            fill={colours[g.key]} onClick={pick(g.key)} {...hover(termsDataKey(g.key))} hide={isHidden(g.label)}
            radius={i === groups.length - 1 ? [4, 4, 0, 0] : undefined} />
        ))}
        <Bar yAxisId="lines" stackId="lines" dataKey="lines" name={FIXED.lines.label} fill={FIXED.lines.colour} radius={[4, 4, 0, 0]} onClick={pick('all')} {...hover('lines')}
          hide={isHidden(FIXED.lines.label)} />
        {mean != null && mean > 0 && !isHidden('Mean Average') && (
          <ReferenceLine yAxisId="value" y={mean} stroke="#f97316" strokeWidth={1.5} strokeDasharray="6 4"
            label={{ value: `Mean Average: ${formatCurrency(mean)}`, position: 'insideTopRight', fill: '#c2410c', fontSize: 11 }} />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  )
}

/** The legend entries for this chart, in the bars' order: arrears, terms, lines. */
export function incomingLegend(groups: CostTermsGroup[], colours: Record<string, string>) {
  return [
    { color: FIXED.arrears_value.colour, label: FIXED.arrears_value.label },
    { color: FIXED.arrears_lines.colour, label: FIXED.arrears_lines.label },
    ...groups.map((g) => ({ color: colours[g.key], label: g.label })),
    { color: FIXED.lines.colour, label: FIXED.lines.label },
    { color: '#f97316', label: 'Mean Average' },
  ]
}
